import { anchorHref, anchorIdOfHref } from './anchors'

/**
 * The three kinds of link a member can make in prose (AB#350, the prose Link dialog): a web page,
 * an e-mail, an anchor of the artefact. The same forms as the slides links (slides/model.ts — kept
 * in line by hand: that module lives in the deck's lazy chunk, which prose code must not pull in).
 */
export type ProseLink =
  | { kind: 'web'; url: string }
  | { kind: 'email'; to: string; subject: string }
  | { kind: 'anchor'; anchorId: string }

/** An e-mail address, as the slides links accept one (slides/model.ts EMAIL_RE). */
const EMAIL_RE = /^[A-Za-z0-9.!#$%&'*+/=^_`{|}~-]+@[A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?(?:\.[A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?)+$/
/** The longest subject of a mailto link (slides/model.ts SUBJECT_MAX). */
export const MAIL_SUBJECT_MAX = 255

export const isEmailAddress = (to: string): boolean => EMAIL_RE.test(to.trim())

/** What a member typed in the Web field, as an href: `https://` is added when no scheme is given
 *  (`host:port/…` counts as no scheme) — the slides' webLinkFrom. */
export function webHrefFrom(input: string): string {
  const v = input.trim()
  return /^[a-z][a-z0-9+.-]*:/i.test(v) && !/^[^:/]+:\d+(\/|$)/.test(v) ? v : `https://${v}`
}

/** `mailto:to[?subject=…]`, the subject encoded. */
export function mailtoHref(to: string, subject = ''): string {
  const s = subject.trim()
  return `mailto:${to.trim()}${s ? `?subject=${encodeURIComponent(s)}` : ''}`
}

/** An href read back into the dialog's kinds — tolerantly: anything else is a web address. */
export function parseProseHref(href: string): ProseLink {
  const v = href.trim()
  const anchorId = anchorIdOfHref(v)
  if (anchorId) return { kind: 'anchor', anchorId }
  if (/^mailto:/i.test(v)) {
    const rest = v.slice('mailto:'.length)
    const q = rest.indexOf('?')
    const decode = (s: string): string => {
      try {
        return decodeURIComponent(s)
      } catch {
        return s
      }
    }
    let subject = ''
    if (q >= 0) {
      for (const pair of rest.slice(q + 1).split('&')) {
        const eq = pair.indexOf('=')
        if (eq >= 0 && decode(pair.slice(0, eq)).toLowerCase() === 'subject') subject = decode(pair.slice(eq + 1))
      }
    }
    return { kind: 'email', to: decode(q < 0 ? rest : rest.slice(0, q)), subject }
  }
  return { kind: 'web', url: v }
}

/** A link's href. */
export function proseLinkHref(l: ProseLink): string {
  if (l.kind === 'anchor') return anchorHref(l.anchorId)
  if (l.kind === 'email') return mailtoHref(l.to, l.subject)
  return webHrefFrom(l.url)
}
